import {
  TRAJECTORY_RING_SIZE,
  TRAJECTORY_STRAIGHTNESS_DIRECT,
  TRAJECTORY_TURN_CIRCLING_DEG,
  TRAJECTORY_REVERSAL_BACKTRACK_DEG,
} from '/bb/constants.js';

/**
 * Pointer trajectory classifier. Holds the latest samples in a ring buffer.
 * Calling `flushSegment` (e.g. on pointerup / idle gap) classifies the captured polyline
 * as direct / circling / backtracking (any combination of flags) and resets the buffer.
 */

export function createTrajectory() {
  return {
    xs: new Float32Array(TRAJECTORY_RING_SIZE),
    ys: new Float32Array(TRAJECTORY_RING_SIZE),
    head: 0,
    len: 0,
    counts: { direct: 0, circling: 0, backtracking: 0, segment_total: 0 },
  };
}

export function trajectoryPush(t, x, y) {
  t.xs[t.head] = x;
  t.ys[t.head] = y;
  t.head = (t.head + 1) % TRAJECTORY_RING_SIZE;
  if (t.len < TRAJECTORY_RING_SIZE) t.len += 1;
}

export function trajectoryFlushSegment(t) {
  if (t.len < 3) {
    t.head = 0;
    t.len = 0;
    return null;
  }
  const xs = new Float32Array(t.len);
  const ys = new Float32Array(t.len);
  const start = (t.head - t.len + TRAJECTORY_RING_SIZE) % TRAJECTORY_RING_SIZE;
  for (let i = 0; i < t.len; i++) {
    const idx = (start + i) % TRAJECTORY_RING_SIZE;
    xs[i] = t.xs[idx];
    ys[i] = t.ys[idx];
  }

  let pathLength = 0;
  let cumulativeTurnDeg = 0;
  let maxReversalDeg = 0;
  let prevAngle = NaN;

  for (let i = 1; i < xs.length; i++) {
    const dx = xs[i] - xs[i - 1];
    const dy = ys[i] - ys[i - 1];
    const seg = Math.hypot(dx, dy);
    pathLength += seg;
    if (seg < 0.5) continue;
    const angle = Math.atan2(dy, dx);
    if (!Number.isNaN(prevAngle)) {
      let diff = angle - prevAngle;
      while (diff > Math.PI) diff -= 2 * Math.PI;
      while (diff < -Math.PI) diff += 2 * Math.PI;
      const absDeg = (Math.abs(diff) * 180) / Math.PI;
      cumulativeTurnDeg += absDeg;
      if (absDeg > maxReversalDeg) maxReversalDeg = absDeg;
    }
    prevAngle = angle;
  }

  const displacement = Math.hypot(xs[xs.length - 1] - xs[0], ys[ys.length - 1] - ys[0]);
  const straightness = pathLength > 0 ? displacement / pathLength : 0;
  const deviationPx = Math.max(0, pathLength - displacement);

  t.counts.segment_total += 1;
  if (straightness >= TRAJECTORY_STRAIGHTNESS_DIRECT) t.counts.direct += 1;
  if (cumulativeTurnDeg >= TRAJECTORY_TURN_CIRCLING_DEG) t.counts.circling += 1;
  if (maxReversalDeg >= TRAJECTORY_REVERSAL_BACKTRACK_DEG) t.counts.backtracking += 1;

  t.head = 0;
  t.len = 0;
  return { deviationPx };
}

export function trajectorySummary(t) {
  return {
    direct: t.counts.direct,
    circling: t.counts.circling,
    backtracking: t.counts.backtracking,
    segment_total: t.counts.segment_total,
  };
}

export function serializeTrajectory(t) {
  return { counts: { ...t.counts } };
}

export function deserializeTrajectory(o) {
  const t = createTrajectory();
  if (o && o.counts) {
    if (Number.isFinite(o.counts.direct)) t.counts.direct = o.counts.direct;
    if (Number.isFinite(o.counts.circling)) t.counts.circling = o.counts.circling;
    if (Number.isFinite(o.counts.backtracking)) t.counts.backtracking = o.counts.backtracking;
    if (Number.isFinite(o.counts.segment_total)) t.counts.segment_total = o.counts.segment_total;
  }
  return t;
}
